/* Home-only carousel enhancement: varied review pool and accessible autoplay. */
(() => {
  'use strict';

  const PRODUCT_POOL = [
    '15548840804676', '15553523319108', '15556530766148', '15557662802244',
    '15558724288836', '15558731137348', '15561225142596', '15561649652036',
    '15561657090372', '15571798622532', '15571810287940', '15575244996932',
    '15576308384068', '15576321098052', '15584133185860', '15585387774276',
    '15585426637124', '15601921098052', '15601928110404', '15636898808132',
    '15639338090820', '15640422416708', '15640501027140', '15641658753348',
    '15643881374020', '15643890581828'
  ];
  const AUTOPLAY_DELAY = 4500;
  const RESUME_DELAY = 8000;
  const SVG_NS = 'http://www.w3.org/2000/svg';

  const randomSeed = () => {
    const stored = sessionStorage.getItem('imr-home-review-seed');
    if (stored) return Number(stored);
    const seed = window.crypto?.getRandomValues
      ? window.crypto.getRandomValues(new Uint32Array(1))[0]
      : Date.now() % 4294967296;
    sessionStorage.setItem('imr-home-review-seed', String(seed));
    return seed;
  };

  const seededRandom = (seed) => () => {
    seed |= 0;
    seed = seed + 0x6D2B79F5 | 0;
    let value = Math.imul(seed ^ seed >>> 15, 1 | seed);
    value = value + Math.imul(value ^ value >>> 7, 61 | value) ^ value;
    return ((value ^ value >>> 14) >>> 0) / 4294967296;
  };

  const shuffle = (items, random) => {
    const result = [...items];
    for (let index = result.length - 1; index > 0; index -= 1) {
      const target = Math.floor(random() * (index + 1));
      [result[index], result[target]] = [result[target], result[index]];
    }
    return result;
  };

  const safePhoto = (review) => {
    const value = Array.isArray(review.photo_urls) ? review.photo_urls[0] : '';
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && /(?:^|\.)shopify\.com$/i.test(url.hostname) ? url.href : '';
    } catch (_) {
      return '';
    }
  };

  const meaningfulReviewText = (review) => {
    const value = String(review.body || '').trim();
    return value && !/^(?:valutazione|recensione) del prodotto$/i.test(value);
  };

  const validReviewDate = (review) => {
    const rawDate = review.review_date || review.created_at;
    const value = String(rawDate || '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(value)
      && !Number.isNaN(new Date(`${value}T12:00:00Z`).getTime());
  };

  // Home is intentionally editorial: unlike product pages, it only shows
  // complete customer stories with a final Shopify CDN photo.
  const isHomeEligible = (review) => Boolean(
    safePhoto(review)
      && meaningfulReviewText(review)
      && review.rating_only !== true
      && validReviewDate(review)
      && Number(review.rating) >= 1
      && Number(review.rating) <= 5
  );

  const starRating = (rating) => {
    const wrapper = document.createElement('span');
    wrapper.className = 'imr-stars';
    wrapper.setAttribute('role', 'img');
    wrapper.setAttribute('aria-label', `${rating} stelle su 5`);
    for (let value = 1; value <= 5; value += 1) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('aria-hidden', 'true');
      svg.classList.add('imr-star');
      if (value <= rating) svg.classList.add('imr-star--on');
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', 'M10 1.7l2.48 5.02 5.54.81-4.01 3.9.95 5.52L10 14.34l-4.96 2.61.95-5.52-4.01-3.9 5.54-.81L10 1.7z');
      svg.append(path);
      wrapper.append(svg);
    }
    return wrapper;
  };

  const reviewCard = (review) => {
    if (!isHomeEligible(review)) return null;
    const card = document.createElement('article');
    card.className = 'imr-carousel__card';
    card.dataset.reviewId = review.id;
    card.dataset.productId = review._productId;

    const image = document.createElement('img');
    image.className = 'imr-carousel__image';
    image.src = safePhoto(review);
    image.loading = 'lazy';
    image.decoding = 'async';
    image.width = 480;
    image.height = 360;
    image.alt = 'Foto della recensione';
    card.append(image);

    const content = document.createElement('div');
    content.className = 'imr-carousel__content';
    content.append(starRating(Math.max(0, Math.min(5, Math.round(Number(review.rating) || 0)))));

    const excerpt = document.createElement('p');
    excerpt.className = 'imr-carousel__excerpt';
    excerpt.textContent = String(review.body || '').trim();
    content.append(excerpt);

    const foot = document.createElement('div');
    foot.className = 'imr-carousel__foot';
    const author = document.createElement('span');
    author.className = 'imr-carousel__author';
    author.textContent = review.author_name || 'Cliente';
    foot.append(author);
    const rawDate = review.review_date || review.created_at;
    if (rawDate) {
      const date = new Date(`${String(rawDate).slice(0, 10)}T12:00:00Z`);
      if (!Number.isNaN(date.getTime())) {
        const time = document.createElement('time');
        time.className = 'imr-carousel__date';
        time.dateTime = String(rawDate).slice(0, 10);
        time.textContent = new Intl.DateTimeFormat('it-IT', {
          day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'
        }).format(date);
        foot.append(time);
      }
    }
    content.append(foot);
    card.append(content);
    return card;
  };

  const interleave = (reviews, limit, random) => {
    const unique = [...new Map(reviews.map((review) => [review.id, review])).values()];
    const photos = shuffle(unique.filter(isHomeEligible), random);
    const result = [];
    let lastProduct = '';

    const take = (bucket) => {
      const preferred = bucket.findIndex((review) => review._productId !== lastProduct);
      return bucket.splice(preferred >= 0 ? preferred : 0, 1)[0];
    };

    while (result.length < limit && photos.length) {
      const review = take(photos);
      result.push(review);
      lastProduct = review._productId;
    }
    return result;
  };

  const enhance = async (widget) => {
    if (widget.dataset.imrHomeEnhanced === 'true') return;
    widget.dataset.imrHomeEnhanced = 'true';
    const track = widget.querySelector('[data-imr-carousel-track]');
    const apiBase = String(widget.dataset.apiBase || '').replace(/\/+$/, '');
    if (!track || !apiBase) return;

    const random = seededRandom(randomSeed());
    const configured = String(widget.dataset.homeProductId || '');
    const candidates = shuffle(PRODUCT_POOL.filter((id) => id !== configured), random);
    const selectedProducts = shuffle([configured, ...candidates.slice(0, 7)].filter(Boolean), random);
    const controller = new AbortController();

    const requests = selectedProducts.map(async (productId) => {
      const url = `${apiBase}/api/reviews/product/${encodeURIComponent(productId)}?limit=4`;
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return [];
      const payload = await response.json();
      return Array.isArray(payload.reviews)
        ? payload.reviews.map((review) => ({ ...review, _productId: productId }))
        : [];
    });

    const settled = await Promise.allSettled(requests);
    const reviews = settled.flatMap((result) => result.status === 'fulfilled' ? result.value : []);
    const display = interleave(reviews, Math.min(Number(widget.dataset.limit) || 12, 16), random);
    if (!display.length) return;

    track.replaceChildren(...display.map(reviewCard).filter(Boolean));
    track.setAttribute('aria-live', 'off');
    widget.hidden = false;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let interval = 0;
    let resumeTimer = 0;
    let transitionTimer = 0;

    const gap = () => parseFloat(getComputedStyle(track).columnGap) || 14;
    const step = () => (track.firstElementChild?.getBoundingClientRect().width || 176) + gap();
    const setScroll = (left, behavior) => track.scrollTo({ left, behavior });

    const normalise = () => {
      const distance = step();
      const offset = Math.max(0, Math.round(track.scrollLeft / distance));
      for (let index = 0; index < offset; index += 1) track.append(track.firstElementChild);
      if (offset) setScroll(Math.max(0, track.scrollLeft - offset * distance), 'auto');
    };

    const next = () => {
      normalise();
      const distance = step();
      setScroll(distance, reducedMotion.matches ? 'auto' : 'smooth');
      window.clearTimeout(transitionTimer);
      transitionTimer = window.setTimeout(() => {
        track.append(track.firstElementChild);
        setScroll(Math.max(0, track.scrollLeft - distance), 'auto');
      }, reducedMotion.matches ? 0 : 650);
    };

    const previous = () => {
      normalise();
      const distance = step();
      track.prepend(track.lastElementChild);
      setScroll(distance, 'auto');
      requestAnimationFrame(() => setScroll(0, reducedMotion.matches ? 'auto' : 'smooth'));
    };

    const stop = () => {
      window.clearInterval(interval);
      interval = 0;
    };

    const start = () => {
      stop();
      if (display.length > 1 && !reducedMotion.matches && !document.hidden) {
        interval = window.setInterval(next, AUTOPLAY_DELAY);
      }
    };

    const pauseThenResume = () => {
      stop();
      window.clearTimeout(resumeTimer);
      if (!reducedMotion.matches) resumeTimer = window.setTimeout(start, RESUME_DELAY);
    };

    widget.addEventListener('click', (event) => {
      const control = event.target.closest('[data-imr-carousel-prev], [data-imr-carousel-next]');
      if (!control) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      pauseThenResume();
      if (control.hasAttribute('data-imr-carousel-prev')) previous();
      else next();
    }, true);
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((name) => {
      track.addEventListener(name, pauseThenResume, { passive: name !== 'keydown' });
    });
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
    reducedMotion.addEventListener?.('change', start);
    start();
  };

  const start = () => {
    if (location.pathname !== '/') return;
    const run = () => document
      .querySelectorAll('immershop-review-carousel[data-home-product-id]')
      .forEach((widget) => enhance(widget).catch(() => {}));
    if (window.customElements?.whenDefined) customElements.whenDefined('immershop-review-carousel').then(run);
    else run();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
